import { ModelNamespaces } from "@granular/authz";
import { Fabric3SidebarItem } from "@granular/fabric3-definitions";
import { GKIconName } from "@granular/gds";

/**
 * This is shown when an app is disabled in the sidebar (Core Nav) and the user
 * hovers over it, *unless* the developers have specified a `disabledTooltip`
 * in their `package.json`
 *
 * TODO: Speak to product about what this should say.
 * TODO: Figure out how we're going to I18n this. This could just be a key 🤷‍♂️
 */
export const APP_DISABLED_MESSAGE = "Coming soon";

/**
 * Show this icon in case people do not specify it when (a) setting up their
 * app or (b) decide to monkey with it later.
 *
 * TODO: This is temporary until we modify `pnpm monorepo:check-package-json`
 * to check for correct values in each app's `package.json` using Zod.
 */
export const APP_ICON_IF_UNSPECIFIED = GKIconName.HelpOutline;

/**
 * Give a name to the current tab so it we can switch between tabs, instead
 * of creating a new one everytime we click on the `Go to Insights/NRE` button
 * on either app. This name is unique and should not ever be reassigned.
 * Classic insights is aware of this name an calls its tab with it.
 * If you change it, it all falls apart, so please don't.
 */
export const CONTAINER_WINDOW_NAME = "granular-insights-browser-tab";

/**
 * We have apps and links that are fixed and always present towards the bottom
 * of the Sidebar/Core Navigation. These are them. In this variable, they're
 * defined exactly like their friends in the `fabric3.sidebar` key in each
 * app's `package.json` file (note the `Fabric3SidebarItem` typing).
 *
 * We transform them to Navigable Items (chiefly with actual icons and
 * translations) just like we do with the apps we discover dynamically when the
 * Container mounts.
 *
 * This is defined separately, exported, and transformed later since we need
 * this simple structure for one thing at the time of this writing: we need to
 * help our Mobile friends by generated a JSON manifest of all the stuff we
 * draw our Sidebar/Core Navigation with. There may be other uses later but
 * that's what we're doing now.
 *
 * Note that these can map to three things:
 *
 * 1) An app (e.g. `apps/profile`)
 *
 * 2) A component. This will have to be defined in `main.tsx`. The routes here
 *    will supersede all of the routing in the Container.
 *
 * 3) An external link (e.g. https://www.corteva.com). You will use an `href`
 *    property for this.
 *
 * Note that if these are apps or components, you will specify their targets in
 * `Container.tsx`. This seems a bit odd and \@nikhilanand is working on a
 * totally refactored version of the sidebar that will avoid this oddness. Ayez
 * la patience...
 */
export const APPS_LOWER_SIMPLE: Array<Partial<Fabric3SidebarItem>> = [
  {
    /* This "route" is a hack for the playwright locators. The page objects look
     * for nav links using their hrefs. If a route has a href of '#',
     * playwright detects multiple matches on the locator and throws an error.
     */
    route: "/help",
    icon: "HelpOutline",
    label: "common_help",
  },
  {
    /**
     * When people log out, make SSO remember the last view they were on and
     * redirect them to this view when they log back in.
     *
     * NOTE: This *may* be annoying with multiple accounts but if you don't
     * have "QA" in your job title, you may not need to deal with this.
     *
     * TODO: Do we just need `location.origin` and nothing more?
     *
     * TODO: Write a test for this.
     */
    route: "/logout",
    icon: "ArrowBack",
    label: "Log_Out",
  },
];

/**
 * For the Homepage at `/` (and when you click the Granular logo ♥️). See the
 * `Homepage.tsx` module for how this is used.
 */

export type HomepageApp = {
  /** Where we send the user. Must match the app's `fabric3.sidebar` route. */
  route: string;
  /**
   * The app's namespace, i.e. the `apps/_____` folder. Used to build the
   * `app-<namespace>` LD flag key that says whether the app is routable.
   *
   * Not derivable from the map key: the key is a permission model
   * (`app_trials_planning`), the namespace is a folder (`trials-planning`).
   */
  namespace: string;
};

/** The apps we're willing to make someone's Homepage. */
export type HomepageAppKey = keyof typeof ModelNamespaces.Fabric3;

/**
 * Which Homepage to show for each value of the `homepage-default-route` LD
 * flag. The flag serves a `ModelNamespaces.Fabric3` key; we look it up here.
 *
 * The key *is* the permission model — `resolveHomepageRoute` hands it straight
 * to `checkAppPermissions`, so there's no separate `permissionModel` field to
 * disagree with it. Typing the key against `ModelNamespaces.Fabric3` means a
 * typo here is a compile error rather than a user quietly landing on
 * `/profile`.
 *
 * `Partial` is the point: anything absent from this map — a missing flag, the
 * `default` variation, an app we haven't added yet — falls through to the
 * permission-based chain in `resolveHomepageRoute`. Adding a route here is an
 * explicit, reviewed act.
 *
 * NOTE: `/profile` is intentionally absent. It's the *absence* of a mapping,
 * not a mapping of its own.
 *
 * `constants.test.ts` checks every entry here against the `package.json` of the
 * app it names: that the app exists and is published, that its `route` is one
 * the Root Router actually serves, and that the key matches the
 * `permissionModel` the app gates *itself* on.
 */
export const HOMEPAGE_ROUTE_BY_APP_KEY: Partial<
  Record<HomepageAppKey, HomepageApp>
> = {
  app_dashboard: { route: "/dashboard", namespace: "dashboard" },
  app_map: { route: "/map", namespace: "map" },
  app_trials_planning: {
    route: "/trials-planning",
    namespace: "trials-planning",
  },
};

/**
 * Narrows an arbitrary LD flag value to a key we actually have a Homepage for.
 *
 * The flag is editable in the LaunchDarkly UI, so its value is untrusted input
 * as far as we're concerned — this is the boundary where it becomes a key.
 */
export const isHomepageAppKey = (value: string): value is HomepageAppKey =>
  Object.hasOwn(HOMEPAGE_ROUTE_BY_APP_KEY, value);
